/**
 * Image Coordinator - Optimize ResponsiveImage performance
 * Reduces N IntersectionObservers + N ResizeObservers to 1 shared of each
 *
 * Benefits:
 * - 99% memory reduction (100 images: 200 observers → 2 observers)
 * - Better scroll performance (1 callback vs N callbacks)
 * - Priority loading (above-fold images first)
 *
 * Browser Support:
 * - IntersectionObserver: Safari 12.1+, Firefox 55+, Edge 15+
 * - ResizeObserver: Safari 13.1+, Firefox 69+, Edge 79+
 * - Fallback included for ResizeObserver
 */

class ImageCoordinator {
  constructor() {
    // Shared observers
    this.io = null; // IntersectionObserver for lazy load
    this.ro = null; // ResizeObserver for size calculations

    // Element tracking
    this.elements = new Map(); // element -> { mode, config, callback }
    this.resizeElements = new Set(); // Elements observing resize
    /** @type {Map<Element, Set<HTMLImageElement>>} Each observed box -> the images sized from it */
    this.resizeTargets = new Map();

    // Configuration
    this.config = {
      // IntersectionObserver config
      lazyLoadMargin: "0px 0px -5%", // Start slightly before entering viewport
      lazyLoadThreshold: 0.01, // Trigger when 1% visible
    };
  }

  /**
   * Initialize shared observers
   */
  init() {
    if (this.io && this.ro) return;

    // Initialize IntersectionObserver for lazy loading
    if (!this.io && "IntersectionObserver" in window) {
      this.io = new IntersectionObserver((entries) => this.handleIntersections(entries), {
        rootMargin: this.config.lazyLoadMargin,
        threshold: this.config.lazyLoadThreshold,
      });
    }

    // Initialize ResizeObserver for dynamic sizes
    if (!this.ro && "ResizeObserver" in window) {
      this.ro = new ResizeObserver((entries) => this.handleResize(entries));
    } else if (!this.ro) {
      // Fallback for Safari < 13.1 (very rare)
      this.setupResizeFallback();
    }
  }

  /**
   * Register image for coordinated lazy loading
   * @param {HTMLImageElement} element - The responsive image
   * @param {Object} options - { mode: 'lazy', onLoad: callback }
   * @returns {Function} Cleanup function
   */
  registerLazyLoad(element, options = {}) {
    if (!this.io) this.init();

    // Fallback if no IntersectionObserver support
    if (!this.io) {
      options.onIntersect?.();
      return () => {};
    }

    // Store element data
    this.elements.set(element, {
      mode: "lazy",
      onIntersect: options.onIntersect,
    });

    // Observe with shared IntersectionObserver
    this.io.observe(element);

    // Return cleanup function
    return () => this.unregisterLazyLoad(element);
  }

  /**
   * Register element for coordinated resize observation
   * @param {HTMLElement} target - The element to observe (wrapper or image)
   * @param {HTMLImageElement} image - The responsive image
   * @param {Function} callback - Called with width when resized
   * @returns {Function} Cleanup function
   */
  registerResize(target, image, callback) {
    if (!this.ro) this.init();

    // Fallback if no ResizeObserver support
    if (!this.ro) {
      // Call immediately with current width
      callback(target.offsetWidth || 0);
      return () => {};
    }

    // Store in elements map
    this.elements.set(image, {
      ...this.elements.get(image),
      resizeTarget: target,
      onResize: callback,
      lastWidth: 0,
    });

    // Add to resize tracking
    this.resizeElements.add(image);

    // Observe target with shared ResizeObserver (a hover image shares its wrapper with the main one)
    let images = this.resizeTargets.get(target);
    if (!images) this.resizeTargets.set(target, (images = new Set()));
    images.add(image);
    this.ro.observe(target);

    // Return cleanup function
    return () => this.unregisterResize(target, image);
  }

  /**
   * Unregister lazy load
   * @param {HTMLImageElement} element
   */
  unregisterLazyLoad(element) {
    if (this.io) {
      this.io.unobserve(element);
    }

    const data = this.elements.get(element);
    if (data && data.mode === "lazy") {
      this.elements.delete(element);
    }
  }

  /**
   * Unregister resize observation
   * @param {HTMLElement} target
   * @param {HTMLImageElement} image
   */
  unregisterResize(target, image) {
    // Stop observing the box once no image is sized from it
    const images = this.resizeTargets.get(target);
    images?.delete(image);
    if (!images?.size) {
      this.resizeTargets.delete(target);
      this.ro?.unobserve(target);
    }

    this.resizeElements.delete(image);

    // Clean up element data if no longer used
    const data = this.elements.get(image);
    if (data && !data.mode) {
      this.elements.delete(image);
    } else if (data) {
      delete data.resizeTarget;
      delete data.onResize;
      delete data.lastWidth;
    }
  }

  /**
   * Handle IntersectionObserver entries
   * @param {IntersectionObserverEntry[]} entries
   */
  handleIntersections(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      const element = entry.target;
      const data = this.elements.get(element);

      if (!data || data.mode !== "lazy") return;

      // Stop observing (one-time load)
      this.io.unobserve(element);

      // Execute callback
      try {
        data.onIntersect?.();
      } catch (error) {
        console.error("[ImageCoordinator] Intersection callback error:", error);
      }

      // Clean up
      this.elements.delete(element);
    });
  }

  /**
   * Handle ResizeObserver entries
   * @param {ResizeObserverEntry[]} entries
   */
  handleResize(entries) {
    // The width is the entry's, so nothing here reads layout, and the callbacks only work out
    // `sizes` from it: they run here, after the layout that sized the box and before the
    // scheduler's IntersectionObserver weighs the images, rather than a frame later
    for (const entry of entries) {
      const images = this.resizeTargets.get(entry.target);
      if (!images) continue;

      const width = Math.ceil(entry.contentRect.width || 0);
      for (const image of images) {
        const data = this.elements.get(image);
        if (!data?.onResize) continue;

        // Only notify if width changed significantly (avoid sub-pixel noise)
        if (Math.abs(width - (data.lastWidth || 0)) < 1) continue;

        data.lastWidth = width;

        try {
          data.onResize(width);
        } catch (error) {
          console.error("[ImageCoordinator] Resize callback error:", error);
        }
      }
    }
  }

  /**
   * Fallback for browsers without ResizeObserver (Safari < 13.1)
   */
  setupResizeFallback() {
    let timeout;
    const handleWindowResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        // Notify all resize-tracked images
        for (const [image, data] of this.elements.entries()) {
          if (!data.resizeTarget || !data.onResize) continue;

          const width = data.resizeTarget.offsetWidth || 0;
          const lastWidth = data.lastWidth || 0;

          if (Math.abs(width - lastWidth) < 1) continue;

          data.lastWidth = width;

          try {
            data.onResize(width);
          } catch (error) {
            console.error("[ImageCoordinator] Resize fallback error:", error);
          }
        }
      }, 100);
    };

    window.addEventListener("resize", handleWindowResize);
  }

  /**
   * Get statistics for debugging
   * @returns {Object}
   */
  getStats() {
    let lazyCount = 0;
    let resizeCount = 0;

    for (const data of this.elements.values()) {
      if (data.mode === "lazy") lazyCount++;
      if (data.resizeTarget) resizeCount++;
    }

    return {
      total: this.elements.size,
      lazy: lazyCount,
      resize: resizeCount,
      observers: {
        io: this.io ? "initialized" : "not initialized",
        ro: this.ro ? "initialized" : "fallback",
      },
      resizeTargets: this.resizeTargets.size,
    };
  }

  /**
   * Destroy coordinator and cleanup
   */
  destroy() {
    // Disconnect observers
    if (this.io) {
      this.io.disconnect();
      this.io = null;
    }

    if (this.ro) {
      this.ro.disconnect();
      this.ro = null;
    }

    // Clear all tracking
    this.elements.clear();
    this.resizeElements.clear();
    this.resizeTargets.clear();
  }
}

// Export singleton instance
export const imageCoordinator = new ImageCoordinator();

// Expose to window for debugging
if (typeof window !== "undefined") {
  window.FoxTheme = window.FoxTheme || {};
  window.FoxTheme.imageCoordinator = imageCoordinator;
}



